import { ChevronDown } from 'lucide-react'
import { LanguageSwitcher } from '@/components/shared/LanguageSwitcher'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Link, useLocation, useNavigate, matchPath } from 'react-router-dom'
import { getCurrentRole, getCurrentUser, logoutApp } from '@/utils/auth'
import { paths } from '@/configs/paths'
import { getRedirectPathByRole } from '@/utils'
import { ROLES } from '@/constants/common'
import { useTranslation } from 'react-i18next'

interface SubNavItem {
  label: string
  href: string
}

export interface NavItemInterface {
  label: string
  href?: string
  subItems?: SubNavItem[]
  activeMatchers?: string[]
}

interface NavItemProps {
  item: NavItemInterface
  pathname: string
}

function NavItem({ item, pathname }: NavItemProps) {
  const { t } = useTranslation()
  const checkActive = (path: string) => {
    const cleanPath = path.split('?')[0]
    return !!matchPath({ path: cleanPath, end: false }, pathname)
  }

  const isMainActive = () => {
    if (item.activeMatchers?.some(checkActive)) return true
    if (item.href && checkActive(item.href)) return true
    return false
  }

  const isDropdownActive = () => {
    if (item.activeMatchers?.some(checkActive)) return true
    if (item.subItems?.some((sub) => checkActive(sub.href))) return true
    return false
  }

  if (item.subItems && item.subItems.length > 0) {
    const active = isDropdownActive()

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={`text-base font-bold transition-colors flex items-center gap-1 cursor-pointer pb-1 ${active && 'text-primary border-b-2 border-primary'}`}
          >
            {t(item.label as any)}
            <ChevronDown className='h-4 w-4' />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start'>
          {item.subItems.map((subItem) => (
            <DropdownMenuItem key={subItem.href} asChild>
              <Link to={subItem.href} className='cursor-pointer'>
                {t(subItem.label as any)}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  // Regular link
  return (
    <Link
      to={item.href || '#'}
      className={`text-base font-bold transition-colors pb-1 ${isMainActive() && 'text-primary border-b-2 border-primary'}`}
    >
      {t(item.label as any)}
    </Link>
  )
}

function HeaderActions() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const handleLogout = () => {
    logoutApp()
    navigate(paths.login.path)
  }

  return (
    <div className='flex items-center gap-4'>
      <LanguageSwitcher />

      {/* User Profile */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className='cursor-pointer bg-secondary text-sm text-primary font-bold rounded-full h-10 w-10 flex items-center justify-center'>
            {getCurrentUser().name?.slice(0, 1)}
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          {/* <DropdownMenuItem>Profile</DropdownMenuItem> */}
          {/* <DropdownMenuItem>Settings</DropdownMenuItem> */}
          <DropdownMenuItem onClick={handleLogout}>{t('Logout' as any)}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export function Header({ navItems }) {
  const { pathname } = useLocation()
  const role = getCurrentRole()

  const homePath = getRedirectPathByRole(role)

  const isHiddenNavigation = role === ROLES.CUSTOMER_ADMIN && pathname === paths.selectMode.path

  return (
    <header className='border-b border-border bg-card fixed top-0 left-0 w-full h-14 z-50'>
      <div className='px-5'>
        {/* Logo and Navigation */}
        <div className='flex items-center justify-between h-14'>
          <Link to={homePath} className='cursor-pointer'>
            <img src='/mroHeader.svg' alt='logo' width='1911' height='270' className='w-40' />
          </Link>

          {/* Navigation Tabs */}
          {!isHiddenNavigation && (
            <nav className='flex gap-4 sm:gap-5 lg:gap-20'>
              {navItems.map((item: any) => (
                <NavItem key={item.label} item={item} pathname={pathname} />
              ))}
            </nav>
          )}

          {/* Right Actions */}
          <HeaderActions />
        </div>
      </div>
    </header>
  )
}
